import { Link, useParams } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { SEO } from "@/lib/seo";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useCity } from "@/contexts/CityContext";
import { useAuth } from "@/contexts/AuthContext";

export default function SpecialistProfile() {
  const { id } = useParams();
  const { city } = useCity();
  const { isAuthenticated, hasAnyRole } = useAuth();
  const isEditingOwn = !id;
  const canMessageRooms = Boolean(id) && isAuthenticated && hasAnyRole("gp", "specialist");

  return (
    <PageShell>
      <SEO
        title={
          isEditingOwn
            ? `My profile · PulseBoard`
            : `Specialist profile · ${city.name} · PulseBoard`
        }
        description="Manage your PulseBoard profile, sub-specialties, and clinical interests."
        path={
          isEditingOwn
            ? `/${city.slug}/specialist-profile`
            : `/${city.slug}/specialist-profile/${id}`
        }
        noindex
      />

      <section className="border-b border-pb-border/60">
        <div className="mx-auto max-w-4xl px-4 py-12 md:px-8">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-pb-primary">
            {isEditingOwn ? "My profile" : `Profile · ${id}`}
          </p>
          <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight md:text-5xl">
            {isEditingOwn ? "Edit your profile" : "Specialist profile"}
          </h1>
          <p className="mt-2 text-pb-ink-2">
            Keep your sub-specialties, clinic addresses, and accepting status
            current. GPs see exactly what you publish — nothing more.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 py-10 md:px-8">
        <div className="grid gap-6">
          {/* Identity */}
          <Card>
            <CardHeader>
              <CardTitle>Identity</CardTitle>
              <CardDescription>
                Your AHPRA registration appears as a badge — the number itself
                is never public.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="name">Display name</Label>
                <Input id="name" className="mt-2" defaultValue="Dr Sarah Chen" />
              </div>
              <div>
                <Label htmlFor="specialty">Primary specialty</Label>
                <Input id="specialty" className="mt-2" defaultValue="Cardiology" />
              </div>
              <div className="md:col-span-2">
                <Label>Verification</Label>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Badge variant="verified">AHPRA number recorded</Badge>
                  <span className="text-sm text-pb-ink-3">
                    Last re-verified 2026-03-12
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sub-specialties + interests */}
          <Card>
            <CardHeader>
              <CardTitle>Sub-specialties + interests</CardTitle>
              <CardDescription>
                Help GPs match patients to your sub-specialty. Be specific.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Label htmlFor="interests">Clinical interests</Label>
              <Input
                id="interests"
                className="mt-2"
                defaultValue="Interventional, heart failure, cardiac imaging"
              />
              <p className="mt-2 text-xs text-pb-ink-3">
                Comma-separated. Avoid superlatives (`best`, `leading`) —
                these are blocked per AHPRA advertising guidelines.
              </p>
            </CardContent>
          </Card>

          {/* Service flags */}
          <Card>
            <CardHeader>
              <CardTitle>Service options</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <ServiceToggle label="Accepting new patients" on={true} />
              <ServiceToggle label="Bulk-billing" on={true} />
              <ServiceToggle label="Telehealth" on={true} />
            </CardContent>
          </Card>

          <div className="flex items-center justify-end gap-3">
            {canMessageRooms ? (
              <Button asChild variant="outline">
                <Link to={`/board/messages/new?facility=${encodeURIComponent(id ?? "")}`}>
                  <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                  Message the rooms
                </Link>
              </Button>
            ) : null}
            <Button variant="outline">Discard</Button>
            <Button>Save changes</Button>
          </div>
        </div>
      </div>
    </PageShell>
  );
}

function ServiceToggle({ label, on }: { label: string; on: boolean }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-pb-border bg-pb-surface px-4 py-3">
      <span className="text-sm font-medium text-pb-ink">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        className={
          on
            ? "relative inline-flex h-6 w-11 items-center rounded-full bg-pb-primary"
            : "relative inline-flex h-6 w-11 items-center rounded-full bg-pb-border-strong"
        }
      >
        <span
          className={
            on
              ? "inline-block h-4 w-4 translate-x-6 rounded-full bg-white transition-transform"
              : "inline-block h-4 w-4 translate-x-1 rounded-full bg-white transition-transform"
          }
        />
      </button>
    </div>
  );
}
